import React, { useState, useEffect, useCallback, useContext, memo, useRef } from 'react';
import axios from 'axios';
import { ShopContext } from '../context/shopcontext';
import Spinner from '../components/Spinner';
import { useNavigate } from 'react-router-dom';
import CategoryCard from '../components/CategoryCard';
import { Helmet } from 'react-helmet';

const Hero = () => {
  const [images, setImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const { backendUrl } = useContext(ShopContext);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [textAnimation, setTextAnimation] = useState(false);
  const imageRef = useRef(null);
  const nextSectionRef = useRef(null);
  const autoPlayRef = useRef(null);

  const navigate = useNavigate();

  // Touch handlers for mobile swipe
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    pauseAutoPlay();
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipeGesture();
    resumeAutoPlay();
  };

  const handleSwipeGesture = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (distance > threshold) {
      nextSlide();
    } else if (distance < -threshold) {
      prevSlide();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const preloadImages = useCallback((imageList) => {
    imageList.forEach(({ url }) => {
      const img = new Image();
      img.src = `${url}?f_auto,q_auto:best,w_1920,h_1080`;
    });
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const res = await axios.get(`${backendUrl}/api/featured/list`);
        if (res.data.success) {
          setImages(res.data.images);

          // Preload first image for faster loading
          if (res.data.images[0]?.url) {
            const preloadLink = document.createElement('link');
            preloadLink.rel = 'preload';
            preloadLink.as = 'image';
            preloadLink.href = `${res.data.images[0].url}?f_auto,q_auto:best,w_1920,h_1080`;
            document.head.appendChild(preloadLink);
          }

          preloadImages(res.data.images);
        }
      } catch (err) {
        console.error('Failed to load jewelry collection images.');
      } finally {
        setLoading(false);
        setIsVisible(true);
      }
    };
    fetchImages();
  }, [backendUrl, preloadImages]);

  // Auto-play functionality
  const startAutoPlay = useCallback(() => {
    if (images.length > 1) {
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, 6000);
    }
  }, [images.length]);

  const pauseAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  };

  const resumeAutoPlay = () => {
    pauseAutoPlay();
    startAutoPlay();
  };

  useEffect(() => {
    startAutoPlay();
    return () => pauseAutoPlay();
  }, [startAutoPlay]);

  const nextSlide = () => {
    setTextAnimation(false);
    setImageLoading(true);
    setTimeout(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
      setTextAnimation(true);
    }, 300);
  };

  const prevSlide = () => {
    setTextAnimation(false);
    setImageLoading(true);
    setTimeout(() => {
      setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
      setTextAnimation(true);
    }, 300);
  };

  const goToSlide = (index) => {
    if (index === currentImage) return;
    setTextAnimation(false);
    setImageLoading(true);
    setTimeout(() => {
      setCurrentImage(index);
      setTextAnimation(true);
    }, 300);
    resumeAutoPlay();
  };

  const scrollToCollection = () => {
    if (nextSectionRef.current) {
      nextSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const firstImageUrl = images[0]?.url ? `${images[0].url}?f_auto,q_auto:best,w_1920,h_1080` : null;

  useEffect(() => {
    setTextAnimation(true);
  }, [currentImage]);

  return (
    <>
      {firstImageUrl && (
        <Helmet>
          <link rel="preload" as="image" href={firstImageUrl} />
        </Helmet>
      )}

      <section className="relative w-full h-[600px] sm:h-[800px] bg-gradient-to-br from-[#fefaf5] via-[#e0c3b5] to-[#b09287] overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-[#b09287]/20 to-[#e0c3b5]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-16 w-48 h-48 bg-gradient-to-br from-[#4c0f2e]/20 to-[#b09287]/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-gradient-to-br from-[#e0c3b5]/20 to-[#fefaf5]/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden relative h-[600px] flex flex-col">
          {/* Image Container */}
          <div className="flex-1 relative overflow-hidden">
            {loading || images.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#fefaf5] to-[#e0c3b5]">
                <Spinner />
              </div>
            ) : (
              <div
                className="absolute inset-0"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <div className="relative w-full h-full">
                  <img
                    ref={imageRef}
                    className={`w-full h-full object-cover transition-all duration-700 ease-out ${
                      isVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'
                    }`}
                    src={`${images[currentImage]?.url}?f_auto,q_auto:best,w_1080,h_1920`}
                    loading="eager"
                    decoding="async"
                    alt="Zarviya Jewelry Collection"
                    onLoad={() => setImageLoading(false)}
                    onError={(e) => {
                      console.error('Failed to load jewelry image:', e.target.src);
                      setImageLoading(false);
                    }}
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2d161e]/70 via-transparent to-[#4c0f2e]/40"></div>

                  {/* Loading Indicator */}
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                      <Spinner />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Content Overlay */}
          <div className="absolute inset-0 flex flex-col justify-between p-6 z-20">
            {/* Top Content */}
            <div className="pt-28 text-center">
              <div
                className={`transition-all duration-1000 ${
                  textAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <h1 className="text-3xl font-cinzelDecorative text-[#ffff] tracking-widest">
                  Zarviya
                </h1>

                <p className="text-sm sm:text-base text-[#fefaf5] tracking-[0.25em] mt-2 uppercase">
                  Crafted to Celebrate You.
                </p>
              </div>
            </div>

            {/* Bottom Content */}
            <div className="text-center space-y-4 pb-6">
              <div
                className={`transition-all duration-1000 delay-300 ${
                  textAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <p className="text-[#ebe2db] text-sm sm:text-base max-w-sm mx-auto leading-relaxed font-serif italic">
                  Adorning moments. Holding stories.
                </p>

                <button
                  onClick={() => navigate('/collection')}
                  className="bg-gradient-to-r from-[#2a081a]  rounded-lg to-[#4c0f2e] hover:from-[#b09287] hover:via-[#e0c3b5] hover:to-[#b09287] text-[#ebe2db] mt-12 px-6 py-2  font-cinzel tracking-widest shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-[#e0c3b5]/30 uppercase text-sm"
                >
                  SHOP
                </button>
              </div>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 pt-4">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentImage === index
                        ? 'bg-[#e0c3b5] scale-125'
                        : 'bg-[#ebe2db]/40 hover:bg-[#e0c3b5]/60'
                    }`}
                    onClick={() => goToSlide(index)}
                    aria-label={`View jewelry collection ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30">
            <button
              onClick={scrollToCollection}
              className="w-12 h-12 bg-[#4c0f2e]/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-[#e0c3b5]/30 hover:bg-[#4c0f2e]/30 transition-all duration-300 animate-bounce"
              aria-label="Scroll to collection"
            >
              <svg
                className="w-5 h-5 text-[#e0c3b5]"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex h-screen items-center">
          {/* Left Side - Content */}
          <div className="w-1/2 px-16 xl:px-24 space-y-8">
            <div
              className={`transition-all duration-1000 ${
                textAnimation ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
            >
              <h1 className="text-6xl xl:text-7xl font-serif text-[#2d161e] mb-4 tracking-wide">
                <span className="bg-gradient-to-r from-[#4c0f2e] to-[#b09287] bg-clip-text text-transparent font-cinzel">
                  ZARVIYA
                </span>
              </h1>
              <p className="text-xl text-[#4c0f2e] font-light tracking-widest uppercase mb-8">
                Timeless Jewelry
              </p>

              <div className="w-24 h-px bg-gradient-to-r from-[#b09287] to-transparent mb-8"></div>

              <p className="text-lg text-[#2d161e] mb-10 max-w-md leading-relaxed">
                Each piece in our collection is meticulously crafted to celebrate life's precious
                moments. From delicate everyday pieces to statement jewelry for special occasions.
              </p>

              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/collection')}
                  className="bg-gradient-to-r from-[#4c0f2e] to-[#2d161e] hover:from-[#4c0f2e]/90 hover:to-[#2d161e]/90 text-[#fefaf5] px-8 py-4 rounded-none font-semibold uppercase tracking-wider shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  Explore Collection
                </button>

                <button
                  onClick={() => navigate('/about')}
                  className="border-2 border-[#4c0f2e] text-[#4c0f2e] hover:bg-[#4c0f2e] hover:text-[#fefaf5] px-8 py-4 rounded-none font-semibold uppercase tracking-wider transition-all duration-300"
                >
                  Our Story
                </button>
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex gap-3 pt-8">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentImage === index
                      ? 'bg-[#4c0f2e] scale-125'
                      : 'bg-[#b09287] hover:bg-[#4c0f2e]'
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`View jewelry collection ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Right Side - Image */}
          <div className="w-1/2 h-full relative overflow-hidden">
            {loading || images.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#fefaf5] to-[#e0c3b5]">
                <Spinner />
              </div>
            ) : (
              <div className="relative w-full h-full">
                <img
                  ref={imageRef}
                  className={`w-full h-full object-cover transition-all duration-1000 ease-out ${
                    isVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'
                  }`}
                  src={`${images[currentImage]?.url}?f_auto,q_auto:best,w_1920,h_1080`}
                  loading="eager"
                  decoding="async"
                  width="1920"
                  height="1080"
                  alt="Zarviya Jewelry Collection"
                  onLoad={() => setImageLoading(false)}
                  onError={(e) => {
                    console.error('Failed to load jewelry image:', e.target.src);
                    setImageLoading(false);
                  }}
                />

                {/* Subtle Overlay */}
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-black/10"></div>

                {/* Loading Indicator */}
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/20 backdrop-blur-sm">
                    <Spinner />
                  </div>
                )}

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-[#4c0f2e]/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-[#e0c3b5]/30 hover:bg-[#4c0f2e]/30 transition-all duration-300 opacity-0 hover:opacity-100 group-hover:opacity-100"
                  aria-label="Previous image"
                >
                  <svg
                    className="w-5 h-5 text-[#fefaf5]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={nextSlide}
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-[#4c0f2e]/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-[#e0c3b5]/30 hover:bg-[#4c0f2e]/30 transition-all duration-300 opacity-0 hover:opacity-100 group-hover:opacity-100"
                  aria-label="Next image"
                >
                  <svg
                    className="w-5 h-5 text-[#fefaf5]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Scroll Indicator for Desktop */}
        <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30">
          <button
            onClick={scrollToCollection}
            className="w-12 h-12 bg-[#4c0f2e]/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-[#e0c3b5]/30 hover:bg-[#4c0f2e]/30 transition-all duration-300 animate-bounce"
            aria-label="Scroll to collection"
          >
            <svg
              className="w-5 h-5 text-[#4c0f2e]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </section>

      {/* Next Section */}
      <div ref={nextSectionRef} className="pt-10 px-4 sm:px-8 ">
        <CategoryCard />
      </div>
    </>
  );
};

export default memo(Hero);
